import moment from 'moment';

export const CARD_EXPIRY_STATES = {
  LEGACY: 'LEGACY',
  FIRST_REMINDER: 'FIRST_REMINDER',
  SECOND_REMINDER: 'SECOND_REMINDER',
  EXPIRED: 'EXPIRED'
};

const DEFAULT_FIRST_REMINDER_DAYS = 60;
const DEFAULT_SECOND_REMINDER_DAYS = 15;

const DATE_FORMAT = 'YYYY-MM-DD';
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

const positiveDayCount = (value, fallback) => (Number.isInteger(value) && value > 0 ? value : fallback);

/**
 * The calendar date an instant falls on, in the library's timezone. Used for both ends of the
 * comparison -- today, and an expiry that arrives as an instant rather than a date.
 *
 * Intl reads the IANA database the platform already ships; moment-timezone would bundle a second
 * copy of it for this one call.
 *
 * A null timezone is a real runtime state, not a defensive hypothetical -- the gateway sends one
 * when the control document cannot be fetched. It falls back to local time, which is what this
 * banner did before CORE-79020; both ends of the comparison fall back together, so the day count
 * stays internally consistent.
 */
export function calendarDateIn(timezone, instant = new Date()) {
  if (timezone) {
    try {
      const parts = Object.fromEntries(
        new Intl.DateTimeFormat('en-US', {
          timeZone: timezone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        })
          .formatToParts(instant)
          .map(({ type, value }) => [type, value])
      );

      return `${parts.year}-${parts.month}-${parts.day}`;
    } catch (error) {
      // Intl.DateTimeFormat raises RangeError on an unknown zone; a bad value from the gateway
      // must not take the sidebar down.
    }
  }

  return moment(instant).format(DATE_FORMAT);
}

/**
 * Whole days between two calendar dates, or null when the expiry date cannot be parsed.
 *
 * `expiryDate` is date-only in every payload in evidence, and a date-only string *is* a calendar
 * date -- it must be taken as written. Pushing it through a timezone would move it: midnight UTC
 * on 2026-06-03 is 2026-06-02 in Toronto. Only the ISO-with-offset form that accountShape and
 * ShortFormattedDate leave open is a real instant, and that one is resolved in the library's
 * timezone, matching v1's card_expiry_local_date.
 */
export function daysUntilExpiry(expiryDate, today, timezone) {
  // ISO_8601 accepts both accepted forms and rejects anything else without moment's
  // "falls back to js Date" deprecation warning.
  const expiry = moment.utc(expiryDate, moment.ISO_8601);
  if (!expiry.isValid()) return null;

  const expiryDay = DATE_ONLY.test(expiryDate) ? expiryDate : calendarDateIn(timezone, expiry.toDate());

  return moment.utc(expiryDay, DATE_FORMAT).diff(moment.utc(today, DATE_FORMAT), 'days');
}

// Day 0 is the expiry day itself and is NOT expired -- the borrower is told the card expires
// today. Mirrors the shipped v1 CardExpiryHelper#card_expiry_state_for, so the two surfaces cannot
// disagree on the one day it matters most.
export function cardExpiryStateFor(daysLeft, firstReminderDays, secondReminderDays) {
  if (daysLeft < 0) return CARD_EXPIRY_STATES.EXPIRED;
  if (daysLeft <= secondReminderDays) return CARD_EXPIRY_STATES.SECOND_REMINDER;
  if (daysLeft <= firstReminderDays) return CARD_EXPIRY_STATES.FIRST_REMINDER;
  return null;
}

// The settings panel rejects second >= first, but that guard is browser-side only and a rejected
// value can still arrive from a hand-edited asset.
function reminderDays(first, second) {
  const firstDays = positiveDayCount(first, DEFAULT_FIRST_REMINDER_DAYS);
  return [firstDays, Math.min(positiveDayCount(second, DEFAULT_SECOND_REMINDER_DAYS), firstDays)];
}

/**
 * The single decision behind the My Borrowing card-expiry row.
 *
 * @returns one of CARD_EXPIRY_STATES, or null for no banner
 */
export default function resolveCardExpiryState({
  expiryDate,
  bannerUpdatesEnabled,
  firstReminderDays,
  secondReminderDays,
  timezone,
  now
}) {
  if (!expiryDate) return null;

  // CARD_EXPIRY_BANNER_REDESIGN off: today's predicate verbatim -- browser-local moment(),
  // hardcoded 60, no lower bound, so an expired card keeps the row it shows now. Live until the
  // flag retires; not dead code.
  if (!bannerUpdatesEnabled) {
    return moment(expiryDate).isBefore(moment(now).add(DEFAULT_FIRST_REMINDER_DAYS, 'days'))
      ? CARD_EXPIRY_STATES.LEGACY
      : null;
  }

  const daysLeft = daysUntilExpiry(expiryDate, calendarDateIn(timezone, now), timezone);
  if (daysLeft === null) return null;

  const [first, second] = reminderDays(firstReminderDays, secondReminderDays);
  return cardExpiryStateFor(daysLeft, first, second);
}
